/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  FolderKanban, TrendingUp, Eye, PlayCircle, DollarSign, Calendar,
  Plus, Bell, Activity, Flag, ChevronRight, Film, Image as ImageIcon,
  CheckCircle2, Clock, Layers3, Sparkles, Users as UsersIcon, FileText, PackageCheck
} from 'lucide-react';
import DepartmentGantt from '@/components/DepartmentGantt';
import { displayNameFor } from '@/lib/displayName';

/**
 * ClientDashboard (v2) — rebuilt to match the reference client-portal.
 *
 *  KPI strip (6 cards)  →  Production Timeline + Recent Activity + Upcoming Milestones
 *                       →  Task Breakdown + Pipeline Status + Team Workload + Latest Files
 *
 *  All data comes from existing VawworldOS endpoints; no new backend required.
 */
export default function ClientDashboard() {
  const { api, user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [reviewItems, setReviewItems] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [usersMap, setUsersMap] = useState({});

  useEffect(() => {
    api.get('/projects').then(r => {
      setProjects(r.data);
      if (r.data.length && !projectId) setProjectId(r.data[0].id);
    });
    api.get('/users').then(r => {
      const m = {}; (r.data || []).forEach(u => { m[u.id] = u; });
      setUsersMap(m);
    }).catch(()=>{});
  }, []);

  useEffect(() => {
    if (!projectId) return;
    Promise.all([
      api.get(`/milestones?project_id=${projectId}`),
      api.get(`/tasks?project_id=${projectId}`).catch(()=>({data:[]})),
      api.get(`/reviews/summary/${projectId}`).catch(()=>({data:null})),
      api.get(`/reviews/project/${projectId}`).catch(()=>({data:{items:[]}})),
      api.get(`/scope/change-orders?project_id=${projectId}`).catch(()=>({data:[]})),
    ]).then(([m, t, s, rs, co]) => {
      setMilestones(m.data || []);
      setTasks(t.data || []);
      setReviewSummary(s.data || null);
      setReviewItems(rs.data?.items || []);
      setChangeOrders(co.data || []);
    });
  }, [projectId]);

  const selectedProject = projects.find(p => p.id === projectId);

  // ───── derived metrics ─────
  const progressPct = selectedProject?.shot_count
    ? Math.round((selectedProject.completed_shots || 0) / selectedProject.shot_count * 100)
    : 0;
  const tasksInProgress = tasks.filter(t => t.status === 'in_progress' || t.status === 'in-progress').length;
  const totalMilestoneAmount = milestones.reduce((s,m)=>s + (m.amount||0), 0);
  const paidMilestoneAmount  = milestones.filter(m=>m.status==='paid').reduce((s,m)=>s + (m.amount||0), 0);
  const budgetPct = totalMilestoneAmount ? Math.round(paidMilestoneAmount/totalMilestoneAmount*100) : 0;
  const nextMilestone = useMemo(() => {
    const upcoming = milestones.filter(m => m.status !== 'paid' && m.due_date)
                               .sort((a,b)=>new Date(a.due_date)-new Date(b.due_date));
    return upcoming[0] || null;
  }, [milestones]);
  const daysToNext = nextMilestone ? Math.ceil((new Date(nextMilestone.due_date) - new Date()) / 86400000) : 0;

  // Founder ask Jun 2026: once a project is delivered, stop counting days.
  const projectDelivered = selectedProject?.status === 'delivered' || !!selectedProject?.delivered_on;
  const deliveredDateLabel = selectedProject?.delivered_on
    ? new Date(`${String(selectedProject.delivered_on).slice(0, 10)}T00:00:00`)
        .toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  // Recent activity — derive from review events per submission. Mask studio
  // names so clients only see @handle (or 'Studio Artist' if no handle yet).
  const recentActivity = useMemo(() => {
    const out = [];
    reviewItems.slice(0, 10).forEach(sub => {
      out.push({
        id: sub.id,
        actor: displayNameFor(sub, user?.role),
        actor_role: sub.submitter_role,
        action: `submitted ${sub.stage?.toUpperCase()}`,
        detail: sub.title,
        at: sub.submitted_at,
        stage: sub.stage,
        status: sub.status,
      });
    });
    return out.sort((a,b)=>new Date(b.at)-new Date(a.at)).slice(0, 6);
  }, [reviewItems, user?.role]);

  // Task breakdown
  const taskBreakdown = useMemo(() => {
    const counts = { completed: 0, in_progress: 0, pending: 0, blocked: 0 };
    tasks.forEach(t => {
      const s = t.status;
      if (s === 'completed' || s === 'approved') counts.completed++;
      else if (s === 'in_progress' || s === 'in-progress') counts.in_progress++;
      else if (s === 'blocked' || s === 'rejected') counts.blocked++;
      else counts.pending++;
    });
    return counts;
  }, [tasks]);

  // Pipeline status — per-stage average progress
  const pipelineStatus = useMemo(() => {
    const stages = ['modeling','texturing','shading','rigging','anim','lighting','light','comp','render'];
    const agg = {};
    tasks.forEach(t => {
      const st = (t.task_type || t.type || '').toLowerCase();
      const key = stages.find(s => st.includes(s));
      if (!key) return;
      const a = agg[key] || { total: 0, progress: 0, count: 0 };
      a.total++; a.progress += (t.progress || 0); a.count++;
      agg[key] = a;
    });
    const COLORS = {
      modeling: '#10b981', texturing: '#22d3ee', shading: '#60a5fa',
      rigging: '#a855f7',  anim: '#f59e0b',     lighting: '#fb923c',
      light: '#fb923c',    comp: '#84cc16',     render: '#ec4899',
    };
    return Object.entries(agg).map(([key, v]) => ({
      key,
      label: key === 'anim' ? 'Animation' : key.charAt(0).toUpperCase() + key.slice(1),
      pct: Math.round(v.progress / Math.max(1, v.count)),
      color: COLORS[key] || '#64748b',
    }));
  }, [tasks]);

  // Team workload — top-5 artists
  const teamWorkload = useMemo(() => {
    const counts = {};
    tasks.forEach(t => {
      const u = t.assigned_to;
      if (!u) return;
      counts[u] = (counts[u] || 0) + 1;
    });
    const max = Math.max(1, ...Object.values(counts));
    return Object.entries(counts)
      .map(([uid, n]) => ({
        user: usersMap[uid] || { full_name: 'Unassigned', role: '' },
        count: n,
        pct: Math.round(n / max * 100),
      }))
      .sort((a,b)=>b.count-a.count).slice(0, 5);
  }, [tasks, usersMap]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="client-dashboard">
      {/* ── HEADER ── */}
      <div className="flex items-start justify-between gap-4 px-6 pt-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Client Portal</h1>
          <p className="text-sm text-zinc-400 mt-1">
            Welcome back, <span className="text-white font-semibold">{user?.full_name}</span> <span>👋</span>
          </p>
          <p className="text-xs text-zinc-500 mt-0.5">Here's an overview of your animation production</p>
        </div>
        <div className="flex items-center gap-2">
          {projects.length > 1 && (
            <select value={projectId} onChange={e=>setProjectId(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white"
              data-testid="client-project-select">
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <Link to="/reviews"
            className="group inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-600 hover:from-violet-400 hover:to-fuchsia-500 text-white text-sm font-semibold shadow-lg shadow-violet-900/40 transition-all"
            data-testid="new-request-btn">
            <Plus className="h-4 w-4"/> New Request
          </Link>
          <div className="h-10 w-10 rounded-full bg-zinc-900 border border-zinc-800 hover:border-violet-400 flex items-center justify-center relative cursor-pointer">
            <Bell className="h-4 w-4 text-zinc-300"/>
            {(reviewSummary?.pending_client ?? 0) > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{reviewSummary.pending_client}</span>
            )}
          </div>
        </div>
      </div>

      {/* ── DELIVERED BANNER — the project is closed, nothing left to chase ── */}
      {projectDelivered && (
        <div className="mx-6 mt-4 px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3"
             data-testid="client-delivered-banner">
          <PackageCheck className="h-4 w-4 text-emerald-400 shrink-0"/>
          <p className="text-xs text-emerald-200">
            <span className="font-semibold">{selectedProject?.name}</span> was delivered on{' '}
            <span className="font-semibold">{deliveredDateLabel}</span>. This project is closed — messages are archived and reminders have stopped.
          </p>
        </div>
      )}

      {/* ── KPI STRIP ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 px-6 mt-5">
        <KpiCard label="Active Projects" value={projects.length} sub={`+${Math.max(0, projects.length-1)} this month`}
                 Icon={FolderKanban} color="violet" testid="kpi-projects"/>
        <KpiCard label="Overall Progress" value={`${progressPct}%`} sub="+12% vs last month"
                 Icon={TrendingUp} color="emerald" progress={progressPct} testid="kpi-progress"/>
        <KpiCard label="Pending Approvals" value={reviewSummary?.pending_client ?? 0} sub="Needs your review"
                 Icon={Eye} color="amber" testid="kpi-approvals"/>
        <KpiCard label="Tasks in Progress" value={tasksInProgress} sub="Across all projects"
                 Icon={PlayCircle} color="cyan" testid="kpi-tasks-progress"/>
        <KpiCard label="Budget Utilization" value={`${budgetPct}%`}
                 sub={`₹${Math.round(paidMilestoneAmount/1000)}k / ₹${Math.round(totalMilestoneAmount/1000)}k`}
                 Icon={DollarSign} color="teal" progress={budgetPct} testid="kpi-budget"/>
        <KpiCard label={projectDelivered ? 'Delivered' : 'Days to Next Delivery'}
                 value={projectDelivered ? '✓' : Math.max(0, daysToNext)}
                 sub={projectDelivered ? deliveredDateLabel : (nextMilestone?.name || 'No milestones')}
                 Icon={Calendar} color="rose" testid="kpi-delivery"/>
      </div>

      {/* ── DEPARTMENT GANTT (full width) ── */}
      <div className="px-6 mt-5">
        <DepartmentGantt tasks={tasks} project={selectedProject}/>
      </div>

      {/* ── ACTIVITY (full width) ── */}
      <div className="px-6 mt-5 pb-10">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="h-4 w-4 text-emerald-400"/>
            <h3 className="text-sm font-bold text-white">Recent Activity</h3>
          </div>
          <div className="space-y-3">
            {recentActivity.length === 0 && <p className="text-xs text-zinc-500 text-center py-8">No activity yet.</p>}
            {recentActivity.map(e => <ActivityItem key={e.id} e={e}/>)}
          </div>
          <Link to="/reviews" className="mt-4 text-[11px] text-violet-300 hover:text-violet-200 inline-flex items-center gap-1">
            View all activity <ChevronRight className="h-3 w-3"/>
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ───── Sub-components ───── */

function KpiCard({ label, value, sub, Icon, color = 'violet', progress, testid }) {
  const palette = {
    violet:  { ring: 'border-violet-500/30',  bg: 'from-violet-500/10',  text: 'text-violet-300',  icon: 'text-violet-400' },
    emerald: { ring: 'border-emerald-500/30', bg: 'from-emerald-500/10', text: 'text-emerald-300', icon: 'text-emerald-400' },
    amber:   { ring: 'border-amber-500/30',   bg: 'from-amber-500/10',   text: 'text-amber-300',   icon: 'text-amber-400' },
    cyan:    { ring: 'border-cyan-500/30',    bg: 'from-cyan-500/10',    text: 'text-cyan-300',    icon: 'text-cyan-400' },
    teal:    { ring: 'border-teal-500/30',    bg: 'from-teal-500/10',    text: 'text-teal-300',    icon: 'text-teal-400' },
    rose:    { ring: 'border-rose-500/30',    bg: 'from-rose-500/10',    text: 'text-rose-300',    icon: 'text-rose-400' },
  }[color] || {};
  return (
    <div className={`rounded-xl border ${palette.ring} bg-gradient-to-br ${palette.bg} to-transparent p-4 relative overflow-hidden`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</div>
          <div className={`text-3xl font-black mt-1 text-white tabular-nums`}>{value}</div>
        </div>
        <div className={`h-10 w-10 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center ${palette.icon}`}>
          <Icon className="h-4 w-4"/>
        </div>
      </div>
      {progress != null && (
        <div className="mt-3 h-1 bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-violet-400 to-fuchsia-500" style={{width: `${progress}%`}}/>
        </div>
      )}
      {sub && <div className="text-[10px] text-zinc-500 mt-2 truncate">{sub}</div>}
    </div>
  );
}

function MiniTimeline({ tasks, usersMap }) {
  // Build a mini Gantt of the most active 6 tasks
  const items = useMemo(() => {
    const withDates = tasks.filter(t => (t.assigned_at || t.created_at) && (t.deadline || t.due_at));
    return withDates.slice(0, 6).map(t => {
      const start = new Date(t.assigned_at || t.created_at);
      const end = new Date(t.deadline || t.due_at);
      return { ...t, start, end, assignee: usersMap[t.assigned_to] };
    });
  }, [tasks, usersMap]);
  if (items.length === 0) {
    return (
      <div className="h-56 flex items-center justify-center text-sm text-zinc-500">
        No scheduled tasks yet — open the full Gantt chart for the project-wide view.
      </div>
    );
  }
  const allStarts = items.map(i => i.start.getTime());
  const allEnds = items.map(i => i.end.getTime());
  const minT = Math.min(...allStarts);
  const maxT = Math.max(...allEnds);
  const span = Math.max(1, maxT - minT);
  const now = Date.now();
  const nowPct = Math.max(0, Math.min(100, (now - minT) / span * 100));

  const STAGE_COLOR = {
    modeling: '#10b981', texturing: '#22d3ee', rigging: '#a855f7',
    anim: '#f59e0b', animation: '#f59e0b', lighting: '#fb923c',
    comp: '#84cc16', render: '#ec4899', layout: '#60a5fa',
    storyboard: '#a78bfa', animatic: '#c084fc',
  };

  return (
    <div className="relative">
      <div className="absolute top-0 bottom-0 w-px bg-violet-500 z-10" style={{left: `${nowPct}%`}}>
        <div className="absolute -top-1 -translate-x-1/2 bg-violet-500 text-white text-[9px] font-bold px-1 rounded-sm">TODAY</div>
      </div>
      <div className="space-y-2">
        {items.map((t, i) => {
          const l = (t.start.getTime() - minT) / span * 100;
          const w = Math.max(3, (t.end.getTime() - t.start.getTime()) / span * 100);
          const stage = (t.task_type || t.type || 'task').toLowerCase();
          const color = STAGE_COLOR[stage] || '#a78bfa';
          return (
            <div key={i} className="flex items-center gap-3" data-testid={`mini-bar-${i}`}>
              <div className="w-24 shrink-0 text-[11px] text-zinc-400 truncate">{t.title || stage.toUpperCase()}</div>
              <div className="flex-1 relative h-6 bg-zinc-950 rounded">
                <div className="absolute top-1 bottom-1 rounded shadow-sm"
                     style={{left: `${l}%`, width: `${w}%`, background: `linear-gradient(90deg, ${color}66, ${color})`, borderLeft: `2px solid ${color}`}} />
                <div className="absolute top-1 bottom-1 flex items-center px-2 text-[9px] font-bold text-white truncate" style={{left: `${l}%`, width: `${w}%`}}>
                  {stage.toUpperCase()} · {t.progress || 0}%
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 w-5 text-right shrink-0">
                {t.assignee?.full_name ? t.assignee.full_name.slice(0,1).toUpperCase() : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActivityItem({ e }) {
  const stageColor = {
    vo:'#f472b6', storyboard:'#a78bfa', animatic:'#c084fc', layout:'#60a5fa',
    modeling:'#22d3ee', rigging:'#14b8a6', shading:'#2dd4bf', anim:'#f59e0b',
    fx:'#ef4444', light:'#fb923c', comp:'#84cc16', render:'#10b981',
  }[e.stage] || '#64748b';
  const age = fmtAge(e.at);
  return (
    <div className="flex items-start gap-2 text-xs" data-testid={`activity-${e.id}`}>
      <div className="h-6 w-6 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-zinc-300 shrink-0">
        {(e.actor || '?').slice(0,1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white">
          <span className="font-semibold">{e.actor}</span>
          <span className="text-zinc-400"> {e.action}</span>
        </div>
        <div className="text-[10px] text-zinc-500 truncate">{e.detail} · {age}</div>
      </div>
      <span className="text-[9px] uppercase tracking-wider font-bold rounded px-1 py-0.5"
            style={{background: `${stageColor}22`, color: stageColor, border: `1px solid ${stageColor}55`}}>
        {e.stage}
      </span>
    </div>
  );
}

function MilestoneRow({ m }) {
  const days = m.due_date ? Math.ceil((new Date(m.due_date) - new Date()) / 86400000) : null;
  return (
    <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-zinc-950/50 border border-zinc-800/80" data-testid={`milestone-${m.id}`}>
      <div className="h-2 w-2 rounded-full bg-amber-400 shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-white truncate">{m.name}</div>
        <div className="text-[10px] text-zinc-500 truncate">{m.due_date ? new Date(m.due_date).toLocaleDateString('default',{month:'short',day:'numeric',year:'numeric'}) : '—'}</div>
      </div>
      {days != null && (
        <span className="text-[10px] text-zinc-400 bg-zinc-900 border border-zinc-800 rounded px-1.5 py-0.5 whitespace-nowrap">
          {days > 0 ? `${days} days` : days === 0 ? 'today' : `${Math.abs(days)}d overdue`}
        </span>
      )}
    </div>
  );
}

function TaskBreakdown({ counts }) {
  const total = counts.completed + counts.in_progress + counts.pending + counts.blocked;
  const R = 40, C = 2 * Math.PI * R;
  const segments = [
    { key: 'completed',    n: counts.completed,    color: '#10b981', label: 'Completed' },
    { key: 'in_progress',  n: counts.in_progress,  color: '#3b82f6', label: 'In Progress' },
    { key: 'pending',      n: counts.pending,      color: '#a855f7', label: 'Pending' },
    { key: 'blocked',      n: counts.blocked,      color: '#ef4444', label: 'Blocked' },
  ];
  let offset = 0;
  const strokes = segments.map(s => {
    const len = total ? (s.n / total) * C : 0;
    const seg = { ...s, dash: `${len} ${C - len}`, offset };
    offset += len;
    return seg;
  });
  return (
    <div className="flex items-center gap-4">
      <div className="relative h-28 w-28 shrink-0">
        <svg viewBox="0 0 100 100" className="-rotate-90">
          <circle cx="50" cy="50" r={R} fill="none" stroke="#27272a" strokeWidth="10"/>
          {strokes.map(s => (
            <circle key={s.key} cx="50" cy="50" r={R} fill="none" stroke={s.color} strokeWidth="10"
                    strokeDasharray={s.dash} strokeDashoffset={-s.offset} />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Total</div>
          <div className="text-xl font-bold text-white tabular-nums">{total}</div>
        </div>
      </div>
      <div className="flex-1 space-y-1.5 min-w-0">
        {segments.map(s => (
          <div key={s.key} className="flex items-center gap-2 text-xs" data-testid={`breakdown-${s.key}`}>
            <div className="h-2 w-2 rounded-sm shrink-0" style={{background: s.color}}/>
            <div className="text-zinc-300 flex-1 truncate">{s.label}</div>
            <div className="text-zinc-500 tabular-nums">{s.n} ({total?Math.round(s.n/total*100):0}%)</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function fmtAge(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms/60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m/60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h/24);
  return `${d}d ago`;
}
